import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type TranslationKey, useT } from '../i18n';
import type { AttachmentLimits } from '../lib/chatAttachments';
import { consumeShare, sharedDraftText, takePendingShare } from '../share';
import { useDrafts } from '../stores/drafts';
import { isSupportedAttachment } from '../uploadAccept';
import { formatAttachmentSize, MAX_FILES_PER_MESSAGE } from './Composer';
import { MenuIcon } from './MenuIcon';

const NOTICE_MS = 10_000;

type T = (key: TranslationKey) => string;

/** The same checks the composer applies to attached files, with the same messages. */
export function acceptSharedFiles(
  files: File[],
  limits: AttachmentLimits | undefined,
  t: T,
): { accepted: File[]; problems: string[] } {
  const maxFile = limits?.maxFileBytes ?? null;
  const unsupported = files.filter((file) => !isSupportedAttachment(file.name));
  const tooLarge = files.filter(
    (file) => isSupportedAttachment(file.name) && maxFile !== null && file.size > maxFile,
  );
  const problems = [
    ...(unsupported.length > 0
      ? [t('attachmentUnsupported').replace('{files}', unsupported.map((f) => f.name).join(', '))]
      : []),
    ...tooLarge.map((file) =>
      t('attachmentFileTooLarge')
        .replace('{filename}', file.name)
        .replace('{limit}', formatAttachmentSize(maxFile ?? 0)),
    ),
  ];
  const fits = files.filter((file) => !unsupported.includes(file) && !tooLarge.includes(file));
  const accepted = fits.slice(0, MAX_FILES_PER_MESSAGE);
  if (fits.length > accepted.length) {
    problems.push(
      t('attachmentTooManyFiles').replace('{count}', String(fits.length - accepted.length)),
    );
  }
  return { accepted, problems };
}

/** Puts the composer in view with the cursor in it once the home page has rendered. */
function focusComposer(): void {
  let tries = 0;
  const attempt = () => {
    const field = document.querySelector<HTMLTextAreaElement>('[data-tour="composer"] textarea');
    if (field) field.focus();
    else if (++tries < 20) window.setTimeout(attempt, 100);
  };
  attempt();
}

/**
 * Signed-in half of "Share to NIVIO": turns the stashed share into a fresh draft on Home and
 * says so. Mounted in the app shell, so a share made while signed out lands after sign-in.
 */
export function ShareIntake({ limits }: { limits: AttachmentLimits | undefined }) {
  const t = useT();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<string | null>(null);

  // The share is taken from storage exactly once, so nothing here may be cancelled halfway
  // (React StrictMode runs effects twice in development).
  useEffect(() => {
    const pending = takePendingShare();
    if (!pending) return;
    (async () => {
      if (pending.kind === 'status') {
        const key = {
          unsupported: 'shareUnsupported',
          toolarge: 'shareTooLarge',
          error: 'shareFailed',
        } as const;
        setNotice(t(key[pending.status]));
        return;
      }
      const content = await consumeShare(pending.id);
      if (!content) {
        setNotice(t('shareExpired'));
        return;
      }
      const { accepted, problems } = acceptSharedFiles(content.files, limits, t);
      // A new draft: what the person shared replaces the old unsent one, the model choice stays.
      useDrafts.getState().update('new', { text: sharedDraftText(content), files: accepted });
      const received = t('shareReceived').replace('{count}', String(accepted.length));
      setNotice([received, ...problems].join(' '));
      navigate('/', { replace: true });
      focusComposer();
    })().catch(() => setNotice(t('shareFailed')));
  }, []);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  if (!notice) return null;
  return (
    <div className="nv-toasts nv-toasts-bottom" role="status" aria-live="polite">
      <div className="nv-toast">
        <div className="nv-toast-body">
          <p className="nv-toast-title">{notice}</p>
        </div>
        <button
          type="button"
          className="nv-toast-close"
          aria-label={t('runNotifyDismiss')}
          onClick={() => setNotice(null)}
        >
          <MenuIcon name="close" />
        </button>
      </div>
    </div>
  );
}
